import type { ReactNode } from 'react';
import Link from 'next/link';
import type { ContentChromeMode } from './ContentModuleShell';

/**
 * Sibling of {@link ContentModuleShell}, narrower in scope. Renders
 * only the title strip; the body lives outside any width constraint
 * so designer-driven module index pages whose body already wraps in
 * a {@code Container} block don't get double-wrapped.
 *
 * <p>Use this for designer-driven module index routes (blog, micro,
 * video landing). Use {@link ContentModuleShell} for routes that
 * still want the 1280px centered body with optional sidebar/aside.
 */
export interface ModuleIndexShellProps {
  /** Title shown in the strip. Suppressed when blank/empty. */
  title?: string;
  /** Subtitle below the title. Hidden in {@code 'minimal'} mode. */
  subtitle?: string;
  /** Uppercase eyebrow above the title. Hidden in {@code 'minimal'} mode. */
  kicker?: string | null;
  /** Optional href that wraps the title in a link. */
  titleHref?: string;
  /** Body-placement search slot. Wrapped in
   *  {@code <search role="search">}. */
  search?: ReactNode | null;
  /** Block-composed body. Rendered below the strip with no additional
   *  width or padding — the body's own Container block owns those. */
  children: ReactNode;
  mode?: ContentChromeMode;
  tone?: 'auto' | 'reading' | 'cinematic';
}

export function ModuleIndexShell({
  title,
  subtitle,
  kicker,
  titleHref,
  search,
  children,
  mode = 'full',
  tone = 'auto',
}: ModuleIndexShellProps) {
  const hasSearch = search !== undefined && search !== null;

  const isMinimal = mode === 'minimal';
  const isImmersive = mode === 'immersive';

  const stripPadY = isMinimal ? 14 : isImmersive ? 36 : 24;
  const titleSize = isMinimal ? 22 : isImmersive ? 44 : 30;
  const titleWeight = isImmersive ? 700 : 600;
  const showKicker = !isMinimal && typeof kicker === 'string' && kicker.length > 0;
  const showTitle = typeof title === 'string' && title.trim().length > 0;
  const showSubtitle = !isMinimal && typeof subtitle === 'string' && subtitle.trim().length > 0;
  const showHeaderStrip = showKicker || showTitle || showSubtitle || hasSearch;

  const palette =
    tone === 'reading'
      ? {
          bg: 'var(--color-surface-subtle, #f9fafb)',
          text: 'var(--color-text-primary, #18181b)',
          muted: 'var(--color-text-tertiary, #71717a)',
          border: 'var(--color-border-default, rgba(0,0,0,0.08))',
          focus: 'var(--color-brand-600, #287f96)',
        }
      : tone === 'cinematic'
        ? {
            bg: 'var(--color-neutral-950, #0a0a0a)',
            text: 'var(--color-text-inverse, #fafafa)',
            muted: 'rgba(250,250,250,0.65)',
            border: 'var(--color-border-on-sunken, rgba(255,255,255,0.12))',
            focus: 'var(--color-brand-300, #74c9d8)',
          }
        : {
            bg: 'var(--color-surface-page, #fafafa)',
            text: 'var(--color-text-primary, #111111)',
            muted: 'var(--color-text-secondary, #71717a)',
            border: 'var(--color-border, rgba(0,0,0,0.08))',
            focus: 'var(--color-brand-600, #4f46e5)',
          };

  const cls = `mis-${mode}`;
  const css = `
    .${cls}-root {
      display: flex;
      flex-direction: column;
      ${isImmersive ? 'min-height: 100vh;' : ''}
      background: ${palette.bg};
      color: ${palette.text};
    }
    .${cls}-strip {
      padding-top: ${stripPadY}px;
      padding-bottom: ${stripPadY}px;
      padding-left: max(24px, env(safe-area-inset-left));
      padding-right: max(24px, env(safe-area-inset-right));
      ${isMinimal ? '' : `border-bottom: 1px solid ${palette.border};`}
    }
    .${cls}-strip-inner {
      max-width: 1280px;
      margin: 0 auto;
      display: flex;
      align-items: ${isMinimal ? 'center' : 'flex-end'};
      justify-content: space-between;
      gap: 24px;
      flex-wrap: wrap;
    }
    .${cls}-search-wrap {
      flex: 0 0 auto;
      min-width: 240px;
    }
    .${cls}-title {
      font-size: ${titleSize}px;
      font-weight: ${titleWeight};
      line-height: 1.15;
      letter-spacing: -0.01em;
      margin: 0;
      color: inherit;
      text-decoration: none;
      ${isImmersive ? 'font-family: var(--font-family-display, "Inter Tight", system-ui, sans-serif);' : ''}
    }
    @media (max-width: 600px) {
      .${cls}-title {
        font-size: ${Math.max(20, titleSize - 8)}px;
      }
    }
    .${cls}-title-link {
      color: inherit;
      text-decoration: none;
    }
    .${cls}-title-link:hover {
      text-decoration: underline;
      text-underline-offset: 4px;
    }
    .${cls}-title-link:focus-visible {
      outline: 2px solid ${palette.focus};
      outline-offset: 4px;
      border-radius: 4px;
    }
    .${cls}-kicker {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: ${palette.muted};
      margin-bottom: 8px;
      font-family: var(--font-family-mono, ui-monospace, "SF Mono", monospace);
    }
    .${cls}-subtitle {
      margin-top: 6px;
      font-size: ${isImmersive ? 16 : 14}px;
      color: ${palette.muted};
      max-width: 720px;
      line-height: 1.5;
    }
    .${cls}-body {
      padding-top: ${isMinimal ? 16 : 32}px;
      padding-bottom: ${isMinimal ? 24 : 56}px;
    }
  `;

  return (
    <div className={`module-index-shell module-index-shell--${mode} ${cls}-root`}>
      <style dangerouslySetInnerHTML={{ __html: css }} />

      {showHeaderStrip && (
        <header className={`${cls}-strip`}>
          <div className={`${cls}-strip-inner`}>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              {showKicker && (
                <div className={`${cls}-kicker`} aria-hidden="true">
                  {kicker}
                </div>
              )}
              {showTitle && <TitleEl title={title!} titleHref={titleHref} cls={cls} />}
              {showSubtitle && <p className={`${cls}-subtitle`}>{subtitle}</p>}
            </div>
            {hasSearch && (
              <search role="search" className={`${cls}-search-wrap`}>
                {search}
              </search>
            )}
          </div>
        </header>
      )}

      <div className={`${cls}-body`}>{children}</div>
    </div>
  );
}

function TitleEl({ title, titleHref, cls }: { title: string; titleHref?: string; cls: string }) {
  if (titleHref) {
    return (
      <h1 className={`${cls}-title`}>
        <Link href={titleHref} className={`${cls}-title-link`}>
          {title}
        </Link>
      </h1>
    );
  }
  return <h1 className={`${cls}-title`}>{title}</h1>;
}
